import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaPlay } from "react-icons/fa";
import { MdOndemandVideo } from "react-icons/md";
import Footer from "../components/footer/footer";
import { api } from "../service/api.service";

const PREVIEW_COUNT = 5;

function formatDisplayDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function hasMeaningfulText(value) {
  return !!value && /[a-zA-Z0-9઀-૿]/.test(value);
}

function categoryKey(rawCategory) {
  const trimmed = (rawCategory || "").trim();
  return trimmed ? trimmed.toLowerCase() : "other";
}

function categoryLabel(rawCategory) {
  const trimmed = (rawCategory || "").trim();
  if (!trimmed) return "Other";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function VideoCard({ thumbnail, title, href, badge, meta }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group block"
    >
      <div className="relative rounded-xl overflow-hidden bg-slate-100 aspect-video">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <MdOndemandVideo className="text-4xl" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/90 text-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <FaPlay className="ml-1" />
          </div>
        </div>
        {badge && (
          <span className="absolute bottom-2 right-2 bg-black/75 text-white text-xs font-medium px-2 py-1 rounded">
            {badge}
          </span>
        )}
      </div>
      <h3
        className="mt-3 text-sm md:text-[15px] font-semibold text-darkgray leading-snug line-clamp-2 group-hover:text-primary transition-colors"
        style={{ fontFamily: '"Philosopher", "Ghanu Bold", sans-serif' }}
      >
        {title}
      </h3>
      {meta && /[a-zA-Z0-9઀-૿]/.test(meta) && (
        <p className="mt-1 text-xs text-lightgray">{meta}</p>
      )}
    </a>
  );
}

function VideoSection({ id, title, count, items, expanded, onToggle }) {
  if (!items || items.length === 0) return null;
  const visibleItems = expanded ? items : items.slice(0, PREVIEW_COUNT);

  return (
    <div id={id} className="mb-14 scroll-mt-40">
      <div className="flex items-end justify-between mb-5">
        <h2
          className="text-2xl md:text-3xl text-primary"
          style={{ fontFamily: '"Philosopher", "Ghanu Bold", sans-serif' }}
        >
          {title}{" "}
          <span className="text-lightgray text-lg align-middle">
            ({count})
          </span>
        </h2>
        {items.length > PREVIEW_COUNT && (
          <button
            type="button"
            onClick={onToggle}
            className="text-primary text-sm font-semibold hover:text-hoverprimary transition-colors whitespace-nowrap"
          >
            {expanded ? "Show Less" : "View All"} ›
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-8">
        {visibleItems.map((item) => (
          <VideoCard key={item.id} {...item} />
        ))}
      </div>
    </div>
  );
}

// A "single" section wraps one series/granth -> show its individual episodes.
// A "multiple" section bundles several playlists -> show one card per playlist.
function buildSectionItems(section) {
  const playlists = [...(section.playlist || [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  );

  if (section.type === "single") {
    const playlist = playlists[0];
    const videos = [...(playlist?.videos || [])]
      .filter((v) => v?.visibility !== false && v?.isDeleted !== true)
      .sort(
        (a, b) =>
          new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)
      );

    return videos.map((video) => ({
      id: video._id,
      thumbnail: video.thumbnailImages?.hqdefault || video.thumbnailImages?.mqdefault,
      title: video.title,
      href: video.url,
      meta: formatDisplayDate(video.date || video.createdAt),
      category: playlist?.category || "",
      vakta: video.vakta || playlist?.vakta || "",
    }));
  }

  return playlists
    .filter((p) => p?.visibility !== false && p?.isDeleted !== true)
    .map((playlist) => ({
      id: playlist._id,
      thumbnail: playlist.thumbnailImages?.hqdefault || playlist.thumbnailImages?.mqdefault,
      title: playlist.name,
      href: playlist.videos?.[0]?.url,
      badge: `${playlist.videos?.length || 0} Videos`,
      meta: playlist.vakta,
      category: playlist.category || "",
      vakta: playlist.vakta || "",
    }));
}

export default function Video() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [categoryFilter, setCategoryFilter] = useState("");
  const [vaktaFilter, setVaktaFilter] = useState("");

  useEffect(() => {
    const fetchSections = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${api.defaults.baseURL}admin/playlist-section-all`,
          { headers: { Accept: "application/json" } }
        );
        const json = await response.json();

        const list = (json?.data || [])
          .filter((s) => s?.isDeleted !== true && (s.playlist?.length || 0) > 0)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

        setSections(list);
      } catch (err) {
        console.error("Error fetching video sections:", err);
        setSections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSections();
  }, []);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const { categoryOptions, vaktaOptions } = useMemo(() => {
    const categoryMap = new Map();
    const vaktaSet = new Set();

    sections.forEach((section) => {
      buildSectionItems(section).forEach((item) => {
        categoryMap.set(categoryKey(item.category), categoryLabel(item.category));
        if (hasMeaningfulText(item.vakta)) {
          vaktaSet.add(item.vakta.trim());
        }
      });
    });

    return {
      categoryOptions: Array.from(categoryMap.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      vaktaOptions: Array.from(vaktaSet).sort((a, b) => a.localeCompare(b)),
    };
  }, [sections]);

  const sectionsWithItems = sections.map((section) => ({
    section,
    items: buildSectionItems(section).filter((item) => {
      const matchesCategory =
        !categoryFilter || categoryKey(item.category) === categoryFilter;
      const matchesVakta = !vaktaFilter || item.vakta?.trim() === vaktaFilter;
      return matchesCategory && matchesVakta;
    }),
  }));

  const totalVisibleItems = sectionsWithItems.reduce(
    (sum, { items }) => sum + items.length,
    0
  );

  const hasActiveFilters = !!categoryFilter || !!vaktaFilter;
  const clearFilters = () => {
    setCategoryFilter("");
    setVaktaFilter("");
  };

  return (
    <div className="bg-white">
      <div className="pt-40 pb-4 max-w-7xl mx-auto px-4">
        <div className="text-sm text-lightgray">
          <Link to="/" className="hover:text-primary transition-colors">
            Home
          </Link>
          <span className="mx-2">›</span>
          <span className="text-primary">Videos</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {loading ? (
          <div className="text-center text-lg text-lightgray py-20">
            Loading videos…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-10">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="text-sm text-darkgray bg-white border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="">All Categories</option>
                {categoryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <select
                value={vaktaFilter}
                onChange={(e) => setVaktaFilter(e.target.value)}
                className="text-sm text-darkgray bg-white border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40 max-w-[260px]"
              >
                <option value="">All Vaktas</option>
                {vaktaOptions.map((vakta) => (
                  <option key={vakta} value={vakta}>
                    {vakta}
                  </option>
                ))}
              </select>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-primary text-sm font-semibold hover:text-hoverprimary transition-colors"
                >
                  Clear Filters ×
                </button>
              )}
            </div>

            {sectionsWithItems.map(({ section, items }) => (
              <VideoSection
                key={section._id}
                id={`section-${section._id}`}
                title={section.name}
                count={items.length}
                items={items}
                expanded={!!expandedSections[section._id]}
                onToggle={() => toggleSection(section._id)}
              />
            ))}

            {sections.length === 0 && (
              <div className="text-center text-lightgray py-20">
                No videos found.
              </div>
            )}

            {sections.length > 0 && totalVisibleItems === 0 && (
              <div className="text-center text-lightgray py-20">
                No videos match the selected filters.
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
